/* imbola-wishlist.js — theme-native wishlist for Imbola (no app, no backend).
 *
 * STORAGE: localStorage key "imbola:wishlist" = JSON array of product handles,
 *          most-recent-first. A handle is enough to fetch /products/{handle}.js
 *          and rebuild a card + URL.
 *
 * PUBLIC API: window.ImbolaWishlist = {all, has, count, add, remove, toggle, clear, setAdapter}
 *
 * SYNC SEAM (sync-ready, not wired now): register a backend/customer-account
 *   adapter later WITHOUT touching this file:
 *       ImbolaWishlist.setAdapter({
 *         push: function (handles) { ...persist to server... },          // called after every change
 *         pull: function () { return fetchHandlesFromServer(); }          // Array | Promise<Array>, merged on init
 *       });
 *   With no adapter (the default) the wishlist is pure localStorage. Every
 *   change also dispatches a document CustomEvent "imbola:wishlist:change"
 *   {detail:{handles}} so other surfaces (or a future sync layer) can react.
 */
(function () {
  var KEY = 'imbola:wishlist';
  var adapter = null;

  function each(list, fn) { Array.prototype.forEach.call(list, fn); }

  function read() {
    try {
      var v = JSON.parse(localStorage.getItem(KEY));
      return Array.isArray(v) ? v.filter(Boolean) : [];
    } catch (e) { return []; }
  }
  function write(list) {
    try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) {}
  }

  function emit(list) {
    try {
      document.dispatchEvent(new CustomEvent('imbola:wishlist:change', { detail: { handles: list.slice() } }));
    } catch (e) {}
  }

  // after a local mutation: persist → push to adapter → notify → repaint
  function changed(list) {
    if (adapter && typeof adapter.push === 'function') {
      try { adapter.push(list.slice()); } catch (e) {}
    }
    emit(list);
    reflect();
  }

  // merge a remote list (from adapter.pull) with the local one, remote first, de-duped
  function merge(remote) {
    if (!Array.isArray(remote)) return;
    var local = read(), seen = {}, out = [];
    remote.concat(local).forEach(function (h) { if (h && !seen[h]) { seen[h] = 1; out.push(h); } });
    write(out);
    emit(out);
    reflect();
    if (document.getElementById('imb-wishlist-grid')) renderPage();
  }

  var API = {
    all: function () { return read(); },
    has: function (h) { return read().indexOf(h) !== -1; },
    count: function () { return read().length; },
    add: function (h) {
      var l = read();
      if (h && l.indexOf(h) === -1) { l.unshift(h); write(l); changed(l); }
      return l;
    },
    remove: function (h) {
      var l = read().filter(function (x) { return x !== h; });
      write(l); changed(l);
      return l;
    },
    toggle: function (h) { return API.has(h) ? API.remove(h) : API.add(h); },
    clear: function () { write([]); changed([]); },
    setAdapter: function (a) {
      adapter = a || null;
      if (adapter && typeof adapter.pull === 'function') {
        try { Promise.resolve(adapter.pull()).then(merge); } catch (e) {}
      }
    }
  };

  /* ── reflect state into the UI (heart buttons + header badge) ── */
  function reflect() {
    var set = {};
    read().forEach(function (h) { set[h] = 1; });
    each(document.querySelectorAll('[data-imb-wish]'), function (btn) {
      btn.setAttribute('aria-pressed', set[btn.getAttribute('data-imb-wish')] ? 'true' : 'false');
    });
    var badge = document.getElementById('wishlist-count');
    if (badge) {
      var n = read().length;
      badge.textContent = n;
      if (n > 0) badge.removeAttribute('hidden'); else badge.setAttribute('hidden', '');
    }
  }

  /* ── heart toggle (event delegation; cards are <a>, so cancel navigation) ── */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-imb-wish]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var h = btn.getAttribute('data-imb-wish');
    if (h) API.toggle(h);
  });

  /* ── wishlist page ── */
  function money(cents) {
    var v = cents / 100;
    var cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'ZAR';
    // match the store's "R 1,250.00" format (comma thousands, period decimal)
    if (cur === 'ZAR') {
      try { return 'R ' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
      catch (e) { return 'R ' + v.toFixed(2); }
    }
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format(v); }
    catch (e) { return v.toFixed(2); }
  }
  function imgUrl(u, w) {
    if (!u) return '';
    return u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }
  function firstVariant(p) {
    var vs = p.variants || [];
    return vs.filter(function (v) { return v.available; })[0] || vs[0] || null;
  }

  var fetched = {}; // handle -> "pending" | true (rendered) | null (gone)

  function insertCard(grid, handle, p) {
    if (read().indexOf(handle) === -1) return;                 // removed while fetching
    if (grid.querySelector('[data-wish-card="' + handle + '"]')) return;
    var v = firstVariant(p);

    var card = document.createElement('div');
    card.className = 'imb-card imb-wish-card';
    card.setAttribute('data-wish-card', handle);

    var media = document.createElement('a');
    media.className = 'imb-card__media';
    media.href = p.url;
    if (p.featured_image) {
      var img = document.createElement('img');
      img.src = imgUrl(p.featured_image, 800);
      img.alt = p.title || '';
      img.loading = 'lazy';
      media.appendChild(img);
    }
    card.appendChild(media);

    var info = document.createElement('div');
    info.className = 'imb-card__info';
    var title = document.createElement('a');
    title.className = 'imb-card__title';
    title.href = p.url;
    title.textContent = p.title || '';
    var price = document.createElement('span');
    price.className = 'imb-card__price';
    price.textContent = money(p.price);
    info.appendChild(title);
    info.appendChild(price);
    card.appendChild(info);

    var actions = document.createElement('div');
    actions.className = 'imb-wish-card__actions';
    var add = document.createElement('button');
    add.type = 'button';
    add.className = 'imb-btn imb-btn--outline imb-wish-add';
    if (v && v.available) { add.setAttribute('data-vid', v.id); add.textContent = 'Add to bag'; }
    else { add.disabled = true; add.textContent = 'Sold out'; }
    var rm = document.createElement('button');
    rm.type = 'button';
    rm.className = 'imb-wish-remove';
    rm.setAttribute('data-imb-wish', handle);
    rm.setAttribute('aria-label', 'Remove from wishlist');
    rm.textContent = 'Remove';
    actions.appendChild(add);
    actions.appendChild(rm);
    card.appendChild(actions);

    grid.appendChild(card);
  }

  function renderPage() {
    var grid = document.getElementById('imb-wishlist-grid');
    if (!grid) return;
    var empty = document.getElementById('imb-wishlist-empty');
    var handles = read();

    // drop cards no longer wished
    each(grid.querySelectorAll('[data-wish-card]'), function (card) {
      if (handles.indexOf(card.getAttribute('data-wish-card')) === -1) card.remove();
    });

    if (!handles.length) {
      if (empty) empty.removeAttribute('hidden');
      grid.setAttribute('hidden', '');
      return;
    }
    if (empty) empty.setAttribute('hidden', '');
    grid.removeAttribute('hidden');

    handles.forEach(function (h) {
      if (grid.querySelector('[data-wish-card="' + h + '"]')) return;
      if (fetched[h] === 'pending') return;
      fetched[h] = 'pending';
      fetch('/products/' + encodeURIComponent(h) + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { if (!r.ok) throw 0; return r.json(); })
        .then(function (p) { fetched[h] = true; insertCard(grid, h, p); })
        .catch(function () { fetched[h] = null; API.remove(h); }); // prune deleted products silently
    });
  }

  /* ── add to bag (wishlist page) — uses the theme's cart drawer ── */
  function addToCart(variantId, btn) {
    var cartDrawer = document.querySelector('cart-drawer');
    var body = { items: [{ id: variantId, quantity: 1 }] };
    if (cartDrawer && cartDrawer.getSectionsToRender) {
      body.sections = cartDrawer.getSectionsToRender().map(function (s) { return s.id; });
      body.sections_url = window.location.pathname;
    }
    if (btn) { btn.disabled = true; btn.classList.add('is-loading'); }
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.json(); })
      .then(function (parsed) {
        if (cartDrawer && parsed && parsed.sections) { cartDrawer.renderContents(parsed); }
        else { window.location.href = '/cart'; }
      })
      .catch(function () { window.location.href = '/cart'; })
      .then(function () { if (btn) { btn.disabled = false; btn.classList.remove('is-loading'); } });
  }
  document.addEventListener('click', function (e) {
    var add = e.target.closest && e.target.closest('.imb-wish-add');
    if (!add || add.disabled) return;
    var vid = add.getAttribute('data-vid');
    if (vid) addToCart(vid, add);
  });

  /* ── boot ── */
  function boot() {
    reflect();
    if (document.getElementById('imb-wishlist-grid')) renderPage();
  }
  window.ImbolaWishlist = API;
  document.addEventListener('imbola:wishlist:change', function () {
    if (document.getElementById('imb-wishlist-grid')) renderPage();
  });
  document.addEventListener('shopify:section:load', reflect); // theme editor
  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
})();
